import React, {useState, useContext} from 'react';
import styled from 'styled-components';
import {Link} from 'react-router-dom';
import {qurl} from '../lib/urlgen';
import {$post, $get} from '../lib/requests';
import {AuthContext} from '../App';
import {openExternal} from './BookViewer';
import {Book} from 'shared';

const FlexCell = styled.div`
  width: 100%;
  padding: 8px;
  box-sizing: border-box;

  @media (min-width: 600px) {
    width: 50%;
  }

  @media (min-width: 960px) {
    width: 33.333%;
  }

  @media (min-width: 1440px) {
    width: 25%;
  }
`;

const StyledLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  &:hover {
    text-decoration: underline;
    color: #0b57d0; // MD3 Primary
  }
`;

const Md3OutlinedCard = styled.div<{$loading?: boolean}>`
  display: flex;
  flex-direction: column;
  height: 100%;
  border-radius: 12px;
  border: 1px solid #c4c7c5; // MD3 outline variant
  background-color: #f8fafd; // MD3 surface
  overflow: hidden;
  transition:
    box-shadow 0.2s,
    background-color 0.2s;
  opacity: ${props => (props.$loading ? 0.6 : 1)};
  font-family: 'odia', 'Roboto', sans-serif;
  color: #1d1b20; // MD3 on-surface

  &:hover {
    box-shadow:
      0px 1px 3px 1px rgba(0, 0, 0, 0.15),
      0px 1px 2px 0px rgba(0, 0, 0, 0.3);
    background-color: #f0f4f9; // Hover surface
  }
`;

const CardActionArea = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  flex-grow: 1;
  cursor: pointer;
  padding: 16px;
`;

const MediaContainer = styled.div`
  width: 100px;
  min-width: 100px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
`;

const MediaImage = styled.img`
  width: 100%;
  max-height: 140px;
  object-fit: contain;
  border-radius: 4px;
`;

const ContentContainer = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  min-width: 0; // Allows text truncation
`;

const Title = styled.h4`
  font-size: 16px;
  font-weight: 500;
  line-height: 1.4;
  margin: 0 0 4px 0;
  color: #1d1b20;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Subtitle = styled.div`
  font-size: 14px;
  color: #49454f; // MD3 on-surface-variant
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const YearSubtitle = styled(Subtitle)`
  margin-bottom: 12px;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 8px;
`;

const BottomTagsContainer = styled(TagsContainer)`
  margin-top: auto;
  margin-bottom: 0;
`;

const CategoryLink = styled(Link)`
  text-decoration: none;
`;

const Md3Chip = styled.span<{$primary?: boolean}>`
  font-size: 12px;
  font-weight: 500;
  border-radius: 8px;
  padding: 2px 8px;
  display: inline-flex;
  align-items: center;
  background-color: ${props =>
    props.$primary ? '#d3e3fd' : '#e1e3e1'}; // Primary vs Surface variant
  color: ${props => (props.$primary ? '#041e49' : '#1d1b20')};
`;

const ClickableChip = styled(Md3Chip)`
  cursor: pointer;
`;

const ActionDivider = styled.div`
  height: 1px;
  background-color: #c4c7c5;
  width: 100%;
`;

const AdminSection = styled.div`
  padding: 12px 16px;
  background-color: #f3f4f9; // Surface container low
`;

const AdminHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: #49454f;
`;

const Md3TextButton = styled.button`
  background: transparent;
  border: none;
  color: #0b57d0; // MD3 Primary
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 100px; // Fully rounded

  &:hover {
    background-color: rgba(11, 87, 208, 0.08); // Primary state layer
  }
`;

const Md3FilledButton = styled.button`
  background: #0b57d0; // MD3 Primary
  color: #ffffff; // On primary
  border: none;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  padding: 6px 16px;
  border-radius: 100px;

  &:hover {
    background: #0842a0; // Hover
  }
`;

const Md3OutlinedButton = styled.button<{$error?: boolean}>`
  background: transparent;
  border: 1px solid ${props => (props.$error ? '#b3261e' : '#79747e')};
  color: ${props => (props.$error ? '#b3261e' : '#0b57d0')};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  padding: 6px 16px;
  border-radius: 100px;

  &:hover {
    background-color: ${props =>
      props.$error ? 'rgba(179, 38, 30, 0.08)' : 'rgba(11, 87, 208, 0.08)'};
  }
`;

const AssignForm = styled.div`
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SelectBox = styled.select`
  padding: 8px 12px;
  border-radius: 4px;
  border: 1px solid #79747e;
  font-size: 14px;
  color: #1d1b20;
  background-color: #fff;
  outline: none;

  &:focus {
    border-color: #0b57d0;
    border-width: 2px;
    padding: 7px 11px; // offset for 2px border
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 8px;
`;

function withSeparator(sep: string, arr: React.ReactNode[]) {
  const out: React.ReactNode[] = [];
  for (let i = 0; i < arr.length; i++) {
    out.push(arr[i]);
    if (i < arr.length - 1) {
      out.push(<React.Fragment key={`sep-${i}`}>{sep}</React.Fragment>);
    }
  }
  return out;
}

export function BookCard({book}: {book: Book}) {
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [volunteers, setVolunteers] = useState<{email: string; role: string}[]>(
    []
  );
  const [selectedVolunteer, setSelectedVolunteer] = useState(
    book.assignedTo || ''
  );
  const {auth} = useContext(AuthContext);
  const isVolunteerOrAdmin = auth?.isAdmin || auth?.user?.role === 'VOLUNTEER';

  async function handlePdfOpen() {
    const isAdmin = auth?.isAdmin;
    const isAssignedToMe = auth?.user?.email === book.assignedTo;

    const {filename} = book;
    if (!filename) return;
    setLoading(true);
    try {
      const res = await $post<{filepath?: string}>('/api/cachedPath', {
        filename,
      });
      if (!(isAdmin || isAssignedToMe)) {
        openExternal(filename);
        return;
      }
      if (res && res.filepath) {
        window.open(`/book/${book.bookid}`, '_blank');
      }
    } catch (err) {
      console.error('Error fetching cached path:', err);
    } finally {
      setLoading(false);
    }
  }

  function handleCardClick(e: React.MouseEvent) {
    if (
      (e.target instanceof HTMLElement && e.target.nodeName === 'A') ||
      (e.target instanceof HTMLElement && e.target.nodeName === 'SPAN') ||
      (e.target instanceof HTMLElement && e.target.nodeName === 'BUTTON') ||
      (e.target instanceof HTMLElement && e.target.closest('.no-click-through'))
    ) {
      return;
    }
    handlePdfOpen();
  }

  function handleCardKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handlePdfOpen();
    }
  }

  async function startAssign(e: React.MouseEvent) {
    e.stopPropagation();
    setAssigning(true);
    const users =
      await $get<{email: string; role: string}[]>('/api/admin/users');
    if (Array.isArray(users)) {
      setVolunteers(users.filter(u => u.role === 'VOLUNTEER'));
    }
  }

  async function handleAssignSubmit(e: React.MouseEvent) {
    e.stopPropagation();
    setLoading(true);
    await $post('/api/admin/assign', {
      bookid: book.bookid,
      email: selectedVolunteer,
    });
    setLoading(false);
    window.location.reload();
  }

  async function handleUnassign(e: React.MouseEvent) {
    e.stopPropagation();
    setLoading(true);
    await $post('/api/admin/assign', {bookid: book.bookid, email: ''});
    setLoading(false);
    window.location.reload();
  }

  return (
    <FlexCell>
      <Md3OutlinedCard $loading={loading}>
        <CardActionArea
          role="button"
          tabIndex={0}
          onClick={handleCardClick}
          onKeyDown={handleCardKeyDown}
        >
          <MediaContainer>
            <MediaImage
              src={`thumbnails/${encodeURIComponent(book.coverImage || '')}`}
              alt={book.name}
            />
          </MediaContainer>
          <ContentContainer>
            <Title>{book.name}</Title>

            <Subtitle>
              {withSeparator(
                ', ',
                book.authors.map((x, i) => (
                  <StyledLink key={x.name || i} to={qurl('ଲେଖକ', x.name)}>
                    {x.name}
                    {x.meta && ` (${x.meta})`}
                  </StyledLink>
                ))
              )}
            </Subtitle>

            <YearSubtitle>
              {withSeparator(
                ', ',
                book.years.map((x, i) => (
                  <StyledLink
                    key={x.year || i}
                    to={qurl('ବର୍ଷ', x.year.toString())}
                  >
                    {x.year}
                  </StyledLink>
                ))
              )}
            </YearSubtitle>

            <TagsContainer>
              <Md3Chip>ପୃଷ୍ଠା: {book.numberOfPages || 0}</Md3Chip>
              {isVolunteerOrAdmin && (
                <Md3Chip $primary>
                  ଡିଜିଟାଲ: {book.pagesLocked?.length || 0}
                </Md3Chip>
              )}
            </TagsContainer>

            <BottomTagsContainer>
              {book.categories.slice(0, 3).map((x, i) => (
                <CategoryLink key={x || i} to={qurl('ବିଷୟ', x)}>
                  <ClickableChip>{x}</ClickableChip>
                </CategoryLink>
              ))}
              {book.categories.length > 3 && (
                <Md3Chip>+{book.categories.length - 3}</Md3Chip>
              )}
            </BottomTagsContainer>
          </ContentContainer>
        </CardActionArea>

        {auth?.isAdmin && (
          <div className="no-click-through">
            <ActionDivider />
            <AdminSection>
              <AdminHeader>
                <span>Assigned: {book.assignedTo || 'None'}</span>
                {!assigning && (
                  <Md3TextButton onClick={startAssign}>
                    {book.assignedTo ? 'Edit assignment' : 'Assign'}
                  </Md3TextButton>
                )}
              </AdminHeader>

              {assigning && (
                <AssignForm>
                  <SelectBox
                    value={selectedVolunteer}
                    onChange={e => setSelectedVolunteer(e.target.value)}
                  >
                    <option value="">-- Select User --</option>
                    {volunteers.map(v => (
                      <option key={v.email} value={v.email}>
                        {v.email}
                      </option>
                    ))}
                  </SelectBox>
                  <ButtonRow>
                    <Md3FilledButton onClick={handleAssignSubmit}>
                      Save
                    </Md3FilledButton>
                    <Md3OutlinedButton $error onClick={handleUnassign}>
                      Unassign
                    </Md3OutlinedButton>
                    <Md3OutlinedButton
                      onClick={e => {
                        e.stopPropagation();
                        setAssigning(false);
                      }}
                    >
                      Cancel
                    </Md3OutlinedButton>
                  </ButtonRow>
                </AssignForm>
              )}
            </AdminSection>
          </div>
        )}
      </Md3OutlinedCard>
    </FlexCell>
  );
}
